import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Briefcase,
  ExternalLink,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  fetchJobBoardAccounts,
  createJobBoardAccount,
  deleteJobBoardAccount,
} from '@/lib/api';
import type { JobBoardAccount } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const JOB_BOARD_PROVIDERS = [
  { value: 'indeed', label: 'Indeed', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'linkedin', label: 'LinkedIn', color: 'bg-sky-500/20 text-sky-400' },
  { value: 'ziprecruiter', label: 'ZipRecruiter', color: 'bg-green-500/20 text-green-400' },
] as const;

function getProviderConfig(provider: string) {
  return (
    JOB_BOARD_PROVIDERS.find((p) => p.value === provider) ?? {
      value: provider,
      label: provider,
      color: 'bg-white/10 text-text-secondary',
    }
  );
}

export function JobBoardSettings() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['job-board-accounts'],
    queryFn: fetchJobBoardAccounts,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteJobBoardAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board-accounts'] });
      addToast({ type: 'success', title: 'Job board account removed' });
      setDeleteConfirmId(null);
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Failed to remove account', message: err.message });
    },
  });

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-bg-tertiary">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Briefcase className="h-5 w-5 text-emerald-500" />
                Job Board Accounts
              </CardTitle>
              <p className="text-sm text-text-tertiary mt-1">
                Connect your job board accounts to post openings directly from the CRM.
              </p>
            </div>
            <Button onClick={() => setShowConnectDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Connect Account
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-bg-secondary rounded-lg border border-white/10">
                  <Skeleton className="h-10 w-10 rounded-lg bg-white/5" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32 bg-white/5" />
                    <Skeleton className="h-3 w-24 bg-white/5" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded-full bg-white/5" />
                </div>
              ))}
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 mx-auto text-text-tertiary mb-4" />
              <p className="text-text-secondary font-medium">No accounts connected</p>
              <p className="text-sm text-text-tertiary mt-1 mb-4">
                Connect a job board account to start posting jobs.
              </p>
              <Button onClick={() => setShowConnectDialog(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Connect Your First Account
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {accounts.map((account: JobBoardAccount) => {
                const config = getProviderConfig(account.provider);
                return (
                  <div
                    key={account.id}
                    className="flex items-center gap-4 p-4 bg-bg-secondary rounded-lg border border-white/10"
                  >
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center font-bold text-sm ${config.color}`}>
                      {config.label.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-text-primary">{config.label}</p>
                      <p className="text-sm text-text-tertiary">
                        Connected {formatDate(account.createdAt)}
                      </p>
                    </div>
                    <Badge
                      className={
                        account.isActive
                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }
                    >
                      {account.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(account.id)}
                      className="text-text-tertiary hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect Account Dialog */}
      <ConnectAccountDialog
        open={showConnectDialog}
        onOpenChange={setShowConnectDialog}
        existingProviders={accounts.map((a: JobBoardAccount) => a.provider)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Job Board Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this job board account? Any active postings
              through this account will need to be managed directly on the job board.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ConnectAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingProviders: string[];
}

function ConnectAccountDialog({
  open,
  onOpenChange,
  existingProviders,
}: ConnectAccountDialogProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [provider, setProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const availableProviders = JOB_BOARD_PROVIDERS.filter(
    (p) => !existingProviders.includes(p.value),
  );

  const createMutation = useMutation({
    mutationFn: () =>
      createJobBoardAccount({
        provider,
        apiKey: apiKey || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board-accounts'] });
      addToast({ type: 'success', title: 'Job board account connected' });
      resetForm();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
      addToast({ type: 'error', title: 'Failed to connect account', message: err.message });
    },
  });

  const resetForm = () => {
    setProvider('');
    setApiKey('');
    setError(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!provider) {
      setError('Please select a job board provider');
      return;
    }

    createMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5 text-emerald-500" />
            Connect Job Board
          </DialogTitle>
          <DialogDescription>
            Connect a job board account to post and manage job listings.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label>Provider *</Label>
            {availableProviders.length === 0 ? (
              <p className="text-sm text-text-tertiary">
                All available job boards are already connected.
              </p>
            ) : (
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a job board" />
                </SelectTrigger>
                <SelectContent>
                  {availableProviders.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="api-key">API Key (optional)</Label>
            <Input
              id="api-key"
              type="password"
              placeholder="Enter your API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-xs text-text-tertiary">
              If required, provide the API key from your job board account settings.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || availableProviders.length === 0}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect Account'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
